import { parsePhoneNumber } from '@getstep/sdk/dist/util/Phones'
import { ErrorMessage } from '@hookform/error-message'
import { captureException } from '@sentry/react'
import classNames from 'classnames'
import get from 'lodash/get'
import { observer } from 'mobx-react-lite'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import { CustomFieldId, useFields } from '../../../graphql/hooks/field'
import type { FormProps } from '../props'
import { STEP_BLACK_APP_DEEPLINK } from './constants'
import styles from './step-black-waitlist.module.scss'
import { TelephoneInput } from '@components/input/telephone-input'
import { TextInput } from '@components/input/text-input'
import { Mobile } from '@components/responsive'
import ShareButton from '@components/share-button/share-button'
import { Text } from '@components/text'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { snackbarStore } from '@lib/store/ui/snackbar'
import { Waitlist, waitlistStore } from '@lib/store/waitlist/WaitlistStore'
import { TEXT } from '@lib/utils/text'
import { WaistlistRequestStatus } from '@lib/utils/waitlist'

const StepBlackWaitlist: FunctionComponent<
    FormProps<{
        email: string
        fullName: string
        phone: string
    }>
> = observer(({ renderSubmit, title, subtitle }) => {
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    useEffectAsync(async () => {
        const isWaitlistOpen = await waitlistStore.isWaitlistOpen(
            Waitlist.StepBlack,
        )
        setIsWaitlistOpen(isWaitlistOpen)
    }, [])

    const handleAlreadyHaveStepClick = () => {
        // nothing will happen if the app is not installed
        window.location.href = STEP_BLACK_APP_DEEPLINK
    }

    const fields = useFields({
        email: CustomFieldId.Email,
        fullName: CustomFieldId.FullName,
        phone: CustomFieldId.Phone,
    })

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, submitCount, isSubmitting },
    } = useForm<{
        email: string
        phone: string
        fullName: string
    }>({
        mode: 'onSubmit',
        defaultValues: {
            email: '',
            fullName: '',
            phone: '',
        },
    })

    const register = (key: keyof typeof fields, options?: RegisterOptions) =>
        ({
            // @ts-ignore: options is too generic, needs to be scoped to fields only
            ...control.register(key, options),
            disabled: isSubmitting,
            error: <ErrorMessage errors={errors} name={key} />,
            label: fields[key].label,
            placeholder: fields[key].placeholder,
            preset: 'material',
            size: 'medium',
            valid: !get(errors, key),
            validationCount: submitCount,
        }) as const

    const onSubmit = handleSubmit(async (values) => {
        try {
            setSuccess(false)

            const parsedPhone = parsePhoneNumber(values.phone)

            const response = await waitlistStore.joinWaitlist(
                Waitlist.StepBlack,
                {
                    email: values.email,
                    phone: parsedPhone.digits,
                    name: values.fullName,
                },
            )

            if (response?.status === WaistlistRequestStatus.ERROR) {
                setError(TEXT.waitlist.error)
                return
            }

            if (response?.status === WaistlistRequestStatus.NOT_FOUND) {
                setError(TEXT.waitlist.notFound)
                return
            }

            if (response?.status === WaistlistRequestStatus.RATE_LIMITED) {
                setError(TEXT.waitlist.rateLimited)
                return
            }

            snackbarStore.show(
                TEXT.waitlist.successSnackbar,
                'waitlist.success',
            )

            reset({
                email: '',
                phone: '',
                fullName: '',
            })
            setSuccess(true)
        } catch (e) {
            captureException(e)
            snackbarStore.show(TEXT.waitlist.error, 'waitlist.error')
        }
    })

    if (!isWaitlistOpen) return null

    return (
        <form
            id='waitlist-form'
            onSubmit={onSubmit}
            data-testid='waitlist.form'
            className={classNames(styles.form)}
        >
            <div className={styles['heading']}>
                <Text
                    tag='h2'
                    typeStyle={{
                        _: 'heading-sm',
                        md: 'heading-xl',
                    }}
                    bold
                >
                    {title}
                </Text>
                <Text className={styles['supporting-text']} tag='div'>
                    {subtitle}
                </Text>
            </div>

            <Mobile className={styles['already-have']}>
                <Text tag='div' color='dim' bold>
                    {TEXT.alreadyHaveStep}{' '}
                    <span
                        className={styles.accent}
                        onClick={handleAlreadyHaveStepClick}
                    >
                        {TEXT.tapHere}
                    </span>
                </Text>
            </Mobile>

            <div className={styles.control}>
                <TextInput
                    className={styles.input}
                    {...register('email', {
                        required: {
                            value: true,
                            message: fields.email.errorTexts.required,
                        },
                        pattern: {
                            value: /[^\s@]+@[^\s@]+\.[^\s@]+/i,
                            message: fields.email.errorTexts.required,
                        },
                    })}
                />
            </div>
            <div className={styles.control}>
                <TelephoneInput
                    className={styles.input}
                    {...register('phone', {
                        required: {
                            value: false,
                            message: fields.phone.errorTexts.required,
                        },
                        validate: async (phone) => {
                            const parsedPhone = parsePhoneNumber(phone)

                            return (
                                parsedPhone.valid ||
                                fields.phone.errorTexts.validate
                            )
                        },
                    })}
                    label={fields.phone.label}
                />
            </div>
            <div className={styles.control}>
                <TextInput
                    className={styles.input}
                    {...register('fullName')}
                    label={fields.fullName.label + ' (optional)'}
                />
            </div>

            <div className={styles.submit}>{renderSubmit(control)}</div>

            {error && (
                <div className={styles['error-container']}>
                    <div
                        className={styles['error-message']}
                        data-testid='waitlist.error'
                    >
                        {error}
                    </div>
                </div>
            )}

            {success && (
                <div className={styles['success-container']}>
                    <div
                        className={styles['success-message']}
                        data-testid='waitlist.success'
                    >
                        {TEXT.waitlist.success}
                    </div>
                    <div className={styles['share-button']}>
                        <ShareButton
                            placement='waistlist.success'
                            title={TEXT.waitlist.STEP_BLACK.shareTitle}
                            text={TEXT.waitlist.STEP_BLACK.shareMessage}
                            url={window.location.href}
                            label={TEXT.shareThisPage}
                        />
                    </div>
                </div>
            )}
        </form>
    )
})

export default StepBlackWaitlist
